import { useMemo, useState, type ReactNode } from 'react';
import { Modal } from './Modal';
import { Button } from './Button';
import { Icon } from './Icon';
import { EmptyState } from './Card';

/**
 * The TopBar's two surfaces — notifications and search.
 *
 * Both are CENTRED MODALS, never side panels: §13.15 gives the desk exactly one
 * side surface, the rail. Both are mounted on every view of a canvas, so they
 * are built once here and configured per role.
 */

/* ══════════════════════════════════════════════════════ notifications */

export interface NotificationRow {
  key: string;
  /** What rang. */
  what: ReactNode;
  /** Which screen of this canvas it lives on. */
  where: string;
  /** Required — see the §8 note below. */
  onGo: () => void;
}

/**
 * NotificationsModal — what the bell opens (deskkit `m_bell`, §13.3).
 *
 * Three rules, all from the shell's own docstring:
 *
 *  1. "Every row is a number that is already on some screen of this canvas, and
 *     every row LANDS on that screen." Hence `onGo` is required — a bell that
 *     opens a list you cannot act on is the §8 dead control in a longer form.
 *  2. "It is NOT the approvals inbox: the inbox is a queue you act in, this is
 *     what rang today."
 *  3. The rows are overridable per view, because "a board whose whole subject is
 *     'nothing is waiting' cannot carry a bell that opens with '4 approvals are
 *     waiting on you'." Pass `rows={[]}` on such a screen and it says so.
 *
 * PRIVACY, and it is not optional: the manager's sub-line reads "Doctors and
 * branches are named, patients never are." A notification crosses the privacy
 * wall the moment it names a patient to someone who should only see numbers.
 */
export function NotificationsModal({
  open,
  onClose,
  rows,
  sub,
  title = 'Notifications',
}: {
  open: boolean;
  onClose: () => void;
  rows: NotificationRow[];
  sub?: ReactNode;
  title?: string;
}) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      sub={sub}
      width={660}
      foot={<Button kind="outline" onClick={onClose}>Close</Button>}
    >
      {rows.length ? (
        <div className="dtable-scroll">
          <table className="dtable">
            <thead>
              <tr>
                <th style={{ width: '74%' }}>What rang</th>
                <th style={{ width: '26%' }}>Where it lives</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.key} className="rowc" onClick={row.onGo}>
                  <td>{row.what}</td>
                  <td className="lnk">{row.where}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <EmptyState title="Nothing rang today" sub="You are up to date." />
      )}
    </Modal>
  );
}

/* ═════════════════════════════════════════════════════════════ search */

export interface SearchHit {
  key: string;
  name: string;
  sub?: string;
  /** Which screen of this canvas holds it. */
  where: string;
  onGo: () => void;
}

export interface SearchGroup {
  key: string;
  label: string;
  hits: SearchHit[];
}

/**
 * SearchOverlay — what the search FIELD opens (§13.3).
 *
 * Scoped to this canvas's own entities and to the role — it is not a global
 * search. Results are GROUPED, and every hit says where it lives, because
 * finding a thing is only half the job if you cannot tell which screen owns it.
 *
 * KNOWN DEFECT IN THE CANVASES, deliberately not reproduced:
 * PROGRESS LOG (9 Sep) records that "the desktop search modal lists results that
 * do not match the query it shows — cosmetic, and it repeats across canvases, so
 * it is one orchestrator-level fix rather than nine." A static artboard can
 * afford that; a live component cannot. So this one FILTERS by the query, and
 * the count line is derived from what is actually shown rather than written by
 * hand. If you hand it `groups`, they are the corpus, not the output.
 */
export function SearchOverlay({
  open,
  onClose,
  groups,
  placeholder = 'Search…',
  scopeNote,
}: {
  open: boolean;
  onClose: () => void;
  groups: SearchGroup[];
  placeholder?: string;
  /** What this search covers — "this clinic's patients and visits". */
  scopeNote?: ReactNode;
}) {
  const [query, setQuery] = useState('');

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return groups;
    return groups
      .map((g) => ({
        ...g,
        hits: g.hits.filter(
          (h) =>
            h.name.toLowerCase().includes(q) ||
            h.sub?.toLowerCase().includes(q) ||
            h.where.toLowerCase().includes(q),
        ),
      }))
      .filter((g) => g.hits.length);
  }, [groups, query]);

  const matches = shown.reduce((n, g) => n + g.hits.length, 0);

  return (
    <Modal open={open} onClose={onClose} title="Search" sub={scopeNote} width={660}>
      <div className="srch" style={{ margin: '12px 0 0' }}>
        <Icon name="search" />
        <input
          className="grow"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={placeholder}
          autoFocus
          style={{ background: 'transparent', border: 'none', outline: 'none', color: 'inherit', font: 'inherit' }}
        />
        {/* Derived from what is shown — never a written-in figure. */}
        <span className="t-cap mut">
          {shown.length} group{shown.length === 1 ? '' : 's'} · {matches} match
          {matches === 1 ? '' : 'es'}
        </span>
      </div>

      {shown.map((group) => (
        <section key={group.key} style={{ marginTop: 16 }}>
          <p className="t-ovl mut mb8">{group.label}</p>
          {group.hits.map((hit) => (
            <button key={hit.key} type="button" className="row w-full text-start" onClick={hit.onGo}>
              <div className="grow">
                <p className="t-strong">{hit.name}</p>
                {hit.sub ? <p className="t-cap mut">{hit.sub}</p> : null}
              </div>
              <span className="t-cap lnk">{hit.where}</span>
            </button>
          ))}
        </section>
      ))}

      {!shown.length ? (
        <div style={{ marginTop: 16 }}>
          <EmptyState title={`Nothing matches “${query}”`} sub="Search covers this canvas only." />
        </div>
      ) : null}
    </Modal>
  );
}
